import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QrCode, Menu, X } from 'lucide-react';
import { Button } from '../Button';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';

export function LandingHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Pricing', path: '/pricing' },
  ];

  const authLinks = [
    { label: 'Sign In', path: '/login' },
  ];

  const renderNavLink = (link: { label: string, path: string }, isAuthLink: boolean = false) => {
    const isActive = location.pathname === link.path;
    
    return (
      <button
        key={link.path}
        onClick={() => navigate(link.path)}
        className={`text-[15px] font-semibold transition-all relative group flex items-center h-11 ${
          isActive 
            ? 'text-[--clinqr-navy]' 
            : isAuthLink || link.path === '/pricing'
              ? 'text-[--clinqr-navy]/60 hover:text-[--clinqr-navy]'
              : 'text-[--clinqr-navy]'
        }`}
      >
        <span className="relative inline-flex flex-col items-center justify-center">
          {/* Invisible bold/italic version to reserve space and prevent layout shift */}
          <span className="invisible h-0 font-serif italic font-bold overflow-hidden" aria-hidden="true">
            {link.label}
          </span>
          <span className={`relative z-10 transition-all duration-300 ${
            isActive 
              ? 'italic font-serif font-bold' 
              : 'group-hover:italic group-hover:font-serif group-hover:font-bold'
          }`}>
            {link.label}
          </span>
          <span className={`absolute bottom-0 left-0 w-full h-[30%] bg-[--clinqr-lime] transition-transform duration-300 -z-10 ${
            isActive 
              ? 'scale-x-100' 
              : 'scale-x-0 group-hover:scale-x-100 origin-left'
          }`}></span>
        </span>
      </button>
    );
  };

  return (
    <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-[--clinqr-navy]/5">
      <div className="container-page py-4 sm:py-5 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
          <div className="w-10 h-10 bg-[--clinqr-navy] rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
            <QrCode className="w-6 h-6 text-[--clinqr-lime]" strokeWidth={2} />
          </div>
          <span className="text-2xl font-display text-[--clinqr-navy] tracking-tight">Clinqr</span>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map(link => renderNavLink(link))}
          
          {isAuthenticated ? (
            <Button variant="primary" className="px-7 h-11 font-bold shadow-[0_10px_20px_-5px_rgba(16,31,28,0.3)] hover:scale-105 transition-transform" onClick={() => navigate(ROUTES.DASHBOARD)}>
              Dashboard
            </Button>
          ) : (
            <div className="flex items-center gap-8">
              {authLinks.map(link => renderNavLink(link, true))}
              <Button variant="primary" className="px-7 h-11 font-bold shadow-[0_10px_20px_-5px_rgba(16,31,28,0.3)] hover:scale-105 transition-transform" onClick={() => navigate('/register')}>
                Get Started
              </Button>
            </div>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-[--clinqr-text-secondary] hover:text-[--clinqr-primary] transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-[--clinqr-border]/10 px-4 py-4 space-y-4">
          <button
            onClick={() => {
              navigate('/');
              setMobileMenuOpen(false);
            }}
            className="block w-full text-left text-sm font-medium text-[--clinqr-primary] py-2"
          >
            Home
          </button>
          <button
            onClick={() => {
              navigate('/pricing');
              setMobileMenuOpen(false);
            }}
            className="block w-full text-left text-sm font-medium text-[--clinqr-text-secondary] hover:text-[--clinqr-primary] py-2"
          >
            Pricing
          </button>
          <div className="pt-4 border-t border-[--clinqr-border]/10 space-y-3">
            {isAuthenticated ? (
              <Button
                variant="primary"
                className="w-full"
                onClick={() => {
                  navigate(ROUTES.DASHBOARD);
                  setMobileMenuOpen(false);
                }}
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => {
                    navigate('/register');
                    setMobileMenuOpen(false);
                  }}
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
